import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Container } from "reactstrap";
import InfiniteScroll from "react-infinite-scroller";
import { useIntl } from "react-intl";
import { useGlobalCore } from "../../cores/globals";
import { useNotificationCore } from "../../cores/notifications";
import { useMeCore } from "../../cores/me";
import { State } from "../../redux/state";
import NotificationContainer from "../component/notifications/NotificationContainer";
import { getNotificationTimestampMs } from "../utils/notificationDisplay";
import "./Notifications.scss";

const Notifications = () => {
  const [hasMore, setHasMore] = useState(true);
  const { company } = useMeCore();
  const intl = useIntl();
  const { isDarkMode } = useGlobalCore();
  const {
    notifications,
    putReadNotifications,
    clearNotifications,
    getNotifications,
  } = useNotificationCore();

  const dismissedIds = useSelector((s: State) =>
    company?.id
      ? s.notification?.dismissedIdsByCompany?.[company.id] ?? []
      : []
  );
  const dismissedSet = useMemo(
    () => new Set(dismissedIds),
    [dismissedIds]
  );

  const sortedNotifications = useMemo(() => {
    return [...notifications]
      .filter((n) => !dismissedSet.has(n.id))
      .sort(
        (a, b) => getNotificationTimestampMs(b) - getNotificationTimestampMs(a)
      );
  }, [notifications, dismissedSet]);

  const unreadCount = useMemo(
    () => sortedNotifications.filter((n) => n.status === 0).length,
    [sortedNotifications]
  );

  const onReadNotifications = useCallback(() => {
    if (!company?.id) return;
    putReadNotifications(company.id);
  }, [putReadNotifications, company]);

  useEffect(() => {
    if (!company?.id) return;
    if (notifications.length > 0) {
      clearNotifications();
    }
    getNotifications(company.id, 1);
  }, [company?.id]);

  return (
    <Container
      fluid
      className={cn("notifications-page px-md-0", {
        "notifications-page--dark": isDarkMode,
      })}
    >
      {notifications.length ? (
        <div className="notifications-page__panel">
          <header className="notifications-page__header">
            <div>
              <h1 className="notifications-page__title">
                {intl.formatMessage({ id: "app.editForm.NotificationTitle" })}
              </h1>
              <p className="notifications-page__subtitle">
                {intl.formatMessage({ id: "app.notifications.pageSubtitle" })}
              </p>
              <div className="notifications-page__meta-row">
                <span className="notifications-page__count-pill">
                  {intl.formatMessage(
                    { id: "app.notifications.itemsTotal" },
                    { count: sortedNotifications.length }
                  )}
                </span>
                {unreadCount > 0 ? (
                  <span className="notifications-page__unread-pill">
                    {intl.formatMessage(
                      { id: "app.notifications.unreadCount" },
                      { count: unreadCount }
                    )}
                  </span>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              className="notifications-page__mark-all"
              onClick={onReadNotifications}
            >
              <FontAwesomeIcon icon={["fas", "check"]} />
              {intl.formatMessage({
                id: "app.navbar.readAllNotifications",
              })}
            </button>
          </header>
          <div className="notifications-page__list">
            <InfiniteScroll
              pageStart={0}
              loadMore={async (page) => {
                if (!company?.id) return;
                const totalDocs = await getNotifications(company.id, page);
                setHasMore(totalDocs !== 0);
              }}
              hasMore={hasMore}
              loader={
                <div className="notifications-page__loader" key={0}>
                  …
                </div>
              }
              useWindow={false}
            >
              {sortedNotifications.map((notification) => (
                <NotificationContainer
                  variant="page"
                  unread={notification.status === 0}
                  key={notification.id}
                  notification={notification}
                />
              ))}
            </InfiniteScroll>
          </div>
        </div>
      ) : (
        <div className="notifications-page__panel">
          <div className="notifications-page__empty">
            {intl.formatMessage({ id: "app.navbar.notNotification" })}
          </div>
        </div>
      )}
    </Container>
  );
};

export default Notifications;
